import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

type AgendorAutoSend = {
  configured: boolean;
  connected: boolean;
  enabled: boolean;
  ownerUserIds: string[];
  users: Array<{ id: string; name: string; email?: string }>;
};

export function AutoSendControl({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [enabled, setEnabled] = useState(false);
  const [ownerUserIds, setOwnerUserIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['agendor-auto-send'], queryFn: () => api.get<AgendorAutoSend>('/integrations/agendor/auto-send'), staleTime: 30_000 });

  useEffect(() => {
    if (!query.data) return;
    setEnabled(query.data.enabled);
    setOwnerUserIds(query.data.ownerUserIds);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => api.put<AgendorAutoSend>('/integrations/agendor/auto-send', { enabled, ownerUserIds }),
    onSuccess: (data) => {
      queryClient.setQueryData(['agendor-auto-send'], data);
      setMessage(data.enabled ? `Autoenvio ativado para ${data.ownerUserIds.length} vendedor(es).` : 'Modo manual ativado.');
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'Não foi possível salvar o autoenvio.'),
  });

  function toggleOwner(ownerUserId: string) {
    setOwnerUserIds((current) => current.includes(ownerUserId) ? current.filter((id) => id !== ownerUserId) : [...current, ownerUserId]);
  }

  const active = query.data?.enabled === true;
  return <div className={'relative'}>
    <button title={active ? 'Autoenvio ativado' : 'Autoenvio em modo manual'} className={'relative flex h-11 items-center gap-2 rounded-xl border px-3 transition ' + (open || active ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800')} onClick={() => { setMessage(''); onOpenChange(!open); }}>
      <Zap className={'h-5 w-5 ' + (active ? 'fill-current' : '')} />
      <span className={'hidden text-xs font-semibold 2xl:inline'}>{active ? 'Autoenvio' : 'Manual'}</span>
      <span className={'absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white dark:border-slate-950 ' + (active ? 'bg-emerald-500' : 'bg-slate-300')} />
    </button>
    {open && <div className={'card absolute right-0 top-14 z-30 w-[min(390px,calc(100vw-24px))] overflow-hidden shadow-2xl'}>
      <div className={'flex items-start justify-between border-b px-4 py-3'}>
        <div><b className={'flex items-center gap-2 text-sm'}><Zap className={'h-4 w-4 text-emerald-600'} /> Autoenvio para o Agendor</b><p className={'mt-1 text-[11px] text-slate-500'}>Distribui novos leads em rodízio entre os vendedores escolhidos.</p></div>
        <button className={'rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'} onClick={() => onOpenChange(false)}><X className={'h-4 w-4'} /></button>
      </div>
      {query.isLoading ? <div className={'grid place-items-center p-10'}><Loader2 className={'h-6 w-6 animate-spin text-emerald-600'} /></div>
        : !query.data?.connected ? <div className={'p-5 text-center'}>
          <p className={'text-sm font-semibold'}>Agendor não conectado</p>
          <p className={'mt-1 text-xs text-slate-500'}>Configure e teste a integração antes de ativar o autoenvio.</p>
          <button className={'mt-4 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white'} onClick={() => { navigate('/integrations'); onOpenChange(false); }}>Abrir integrações</button>
        </div> : <>
          <div className={'border-b bg-slate-50 p-3 dark:bg-slate-950/50'}>
            <button className={'flex w-full items-center justify-between rounded-xl border bg-white px-3 py-3 text-left dark:bg-slate-900'} onClick={() => setEnabled((value) => !value)}>
              <span><b className={'block text-xs'}>Envio automático</b><small className={'text-[10px] text-slate-500'}>{enabled ? 'Novos leads serão enviados automaticamente' : 'Os envios continuam sendo feitos manualmente'}</small></span>
              <span className={'h-6 w-11 rounded-full p-1 transition ' + (enabled ? 'bg-emerald-500' : 'bg-slate-300')}><span className={'block h-4 w-4 rounded-full bg-white shadow transition ' + (enabled ? 'translate-x-5' : '')} /></span>
            </button>
          </div>
          <div className={'p-4'}>
            <div className={'mb-3 flex items-center justify-between'}>
              <div><b className={'text-xs'}>Vendedores do Agendor</b><p className={'text-[10px] text-slate-500'}>O rodízio respeita a ordem selecionada.</p></div>
              <span className={'rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300'}>{ownerUserIds.length} selecionado(s)</span>
            </div>
            <div className={'max-h-60 space-y-2 overflow-y-auto pr-1'}>{query.data.users.map((owner) => {
              const selected = ownerUserIds.includes(owner.id);
              return <button key={owner.id} className={'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ' + (selected ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800')} onClick={() => toggleOwner(owner.id)}>
                <span className={'grid h-5 w-5 shrink-0 place-items-center rounded-md border ' + (selected ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300')}>{selected && <Check className={'h-3.5 w-3.5'} />}</span>
                <span className={'min-w-0'}><b className={'block truncate text-xs'}>{owner.name}</b><small className={'block truncate text-[10px] text-slate-500'}>{owner.email || 'ID ' + owner.id}</small></span>
              </button>;
            })}</div>
            {message && <p className={'mt-3 rounded-lg px-3 py-2 text-xs ' + (save.isError ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300')}>{message}</p>}
            <button className={'mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50'} disabled={save.isPending || (enabled && !ownerUserIds.length)} onClick={() => save.mutate()}>
              {save.isPending && <Loader2 className={'h-4 w-4 animate-spin'} />}Salvar modo de envio
            </button>
          </div>
        </>}
    </div>}
  </div>;
}
